import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import logo from '../../logo.png';
import { ArrowLeft } from 'lucide-react';

const TIPO_COLORES = {
  BASICA:        { bg: '#1a3a6b', label: 'BÁSICA' },
  CONTIGUA:      { bg: '#2563a8', label: 'CONTIGUA' },
  ESPECIAL:      { bg: '#7c3aed', label: 'ESPECIAL' },
  EXTRAORDINARIA:{ bg: '#b45309', label: 'EXTRAORDINARIA' },
  EXT_CONTIGUA:  { bg: '#92400e', label: 'EXT. CONTIGUA' },
};
const INTERVALO = 30;
const hora = (ts) => new Date(ts).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
const colorPct = (pct) => { const p = parseFloat(pct||0); if(p>=80) return 'text-green-600'; if(p>=50) return 'text-yellow-600'; if(p>=25) return 'text-orange-500'; return 'text-red-600'; };
const bgBarPct = (pct) => { const p = parseFloat(pct||0); if(p>=80) return 'bg-green-500'; if(p>=50) return 'bg-yellow-500'; if(p>=25) return 'bg-orange-500'; return 'bg-red-500'; };

// ── GESTIÓN DE CASILLAS ──────────────────────
function GestionCasillas() {
  const [casillas, setCasillas] = useState([]);
  const [secciones, setSecciones] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [tipoRep, setTipoRep] = useState('');
  const [loading, setLoading] = useState(true);
  const [filtroSeccion, setFiltroSeccion] = useState('');
  const [filtroBuscar, setFiltroBuscar] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroSinRep, setFiltroSinRep] = useState(false);
  const [modalRep, setModalRep] = useState(null);
  const [formRep, setFormRep] = useState({ user_id: '', nombre_completo: '', telefono: '', notas: '' });
  const [guardando, setGuardando] = useState(false);
  const [mensaje, setMensaje] = useState(null);

  const cargarDatos = useCallback(async () => {
    setLoading(true);
    try {
      const [rC, rS, rU] = await Promise.all([
        api.get('/casillas'),
        api.get('/casillas/secciones/lista'),
        api.get('/auth/usuarios/para-casillas'),
      ]);
      setCasillas(rC.data.casillas || []);
      setSecciones(rS.data.secciones || []);
      setUsuarios(rU.data.usuarios || []);
    } catch(e){ console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { cargarDatos(); }, [cargarDatos]);

  const casillasFiltradas = casillas.filter(c => {
    if (filtroSeccion && String(c.seccion) !== filtroSeccion) return false;
    if (filtroTipo && c.tipo_base !== filtroTipo) return false;
    if (filtroSinRep && c.rep_nombre) return false;
    if (filtroBuscar) { const q = filtroBuscar.toLowerCase(); return c.nombre_lugar?.toLowerCase().includes(q) || c.localidad?.toLowerCase().includes(q) || c.rep_nombre?.toLowerCase().includes(q) || String(c.seccion).includes(q); }
    return true;
  });

  const porSeccion = casillasFiltradas.reduce((acc, c) => { if(!acc[c.seccion]) acc[c.seccion]=[]; acc[c.seccion].push(c); return acc; }, {});

  const abrirModal = (c) => { setModalRep(c); setFormRep({ user_id: c.usuario_id||'', nombre_completo: c.rep_nombre||'', telefono: c.rep_telefono||'', notas: '' }); };

  const handleSelectUsuario = (uid) => {
    const u = usuarios.find(u => u.id === uid);
    if (u) setFormRep(p => ({ ...p, user_id: uid, nombre_completo: `${u.nombre} ${u.apellido_paterno} ${u.apellido_materno||''}`.trim(), telefono: u.telefono||'' }));
    else setFormRep(p => ({ ...p, user_id: '' }));
  };

  const guardar = async () => {
    if (!formRep.nombre_completo.trim()) { setMensaje({ tipo:'error', texto:'Nombre obligatorio' }); return; }
    setGuardando(true);
    try {
      await api.post(`/casillas/${modalRep.id}/representante`, formRep);
      await cargarDatos(); setModalRep(null);
      setMensaje({ tipo:'exito', texto:'Representante asignado.' });
      setTimeout(() => setMensaje(null), 3000);
    } catch(e){ setMensaje({ tipo:'error', texto: e.response?.data?.error||e.message }); }
    finally { setGuardando(false); }
  };

  const quitar = async (c) => {
    if (!window.confirm(`¿Quitar representante de ${c.seccion} ${c.tipo_casilla}?`)) return;
    try { await api.delete(`/casillas/${c.id}/representante`); await cargarDatos(); } catch(e){ alert(e.message); }
  };

  const total = casillas.length;
  const conRep = casillas.filter(c => c.rep_nombre).length;
  const pct = total ? Math.round(conRep*100/total) : 0;

  if (loading) return <div className="flex items-center justify-center h-64"><p className="text-gray-400">Cargando casillas...</p></div>;

  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        {[{l:'Total',v:total,c:'text-blue-600'},{l:'Con rep.',v:conRep,c:'text-green-600'},{l:'Sin asignar',v:total-conRep,c:total-conRep>0?'text-red-600':'text-green-600'},{l:'Votos hoy',v:casillas.reduce((s,c)=>s+parseInt(c.votos_marcados||0),0),c:'text-purple-600'}].map(s=>(
          <div key={s.l} className="bg-white rounded-xl shadow-sm p-4 border border-gray-100 text-center">
            <p className={`text-2xl font-bold ${s.c}`}>{s.v}</p>
            <p className="text-xs text-gray-500 mt-1">{s.l}</p>
          </div>
        ))}
      </div>
      <div className="bg-white rounded-xl shadow-sm p-4 mb-4 border border-gray-100">
        <div className="flex justify-between text-sm mb-2"><span className="text-gray-500">Representantes asignados</span><span className="font-bold text-gray-700">{conRep}/{total} ({pct}%)</span></div>
        <div className="w-full bg-gray-100 rounded-full h-3"><div className="bg-green-500 h-3 rounded-full transition-all" style={{width:`${pct}%`}}/></div>
      </div>
      {mensaje && <div className={`mb-4 p-3 rounded-xl text-sm ${mensaje.tipo==='exito'?'bg-green-50 border border-green-200 text-green-800':'bg-red-50 border border-red-200 text-red-800'}`}>{mensaje.texto}</div>}
      <div className="flex flex-wrap gap-3 mb-4">
        <input className="flex-1 min-w-48 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" placeholder="🔍 Buscar..." value={filtroBuscar} onChange={e=>setFiltroBuscar(e.target.value)}/>
        <select className="px-3 py-2 border border-gray-300 rounded-lg text-sm" value={filtroSeccion} onChange={e=>setFiltroSeccion(e.target.value)}>
          <option value="">Todas las secciones</option>
          {secciones.map(s=><option key={s.seccion} value={s.seccion}>Sección {s.seccion}</option>)}
        </select>
        <select className="px-3 py-2 border border-gray-300 rounded-lg text-sm" value={filtroTipo} onChange={e=>setFiltroTipo(e.target.value)}>
          <option value="">Todos los tipos</option>
          {Object.entries(TIPO_COLORES).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
        </select>
        <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
          <input type="checkbox" checked={filtroSinRep} onChange={e=>setFiltroSinRep(e.target.checked)}/> Solo sin representante
        </label>
      </div>
      {Object.entries(porSeccion).map(([sec, items]) => {
        const conRepSec = items.filter(i=>i.rep_nombre).length;
        return (
          <div key={sec} className="bg-white rounded-xl shadow-sm mb-4 overflow-hidden border border-gray-100">
            <div className="flex items-center gap-4 px-4 py-3 bg-gray-50 border-b">
              <span className="font-bold text-blue-700 text-sm">Sección {sec}</span>
              <span className="text-xs text-gray-400 flex-1">{items[0]?.localidad||''}</span>
              <span className={`text-xs font-bold px-3 py-1 rounded-full text-white ${conRepSec===items.length?'bg-green-600':conRepSec===0?'bg-red-600':'bg-yellow-600'}`}>{conRepSec}/{items.length} asignados</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>{['Casilla','Tipo','Lugar','Representante','Teléfono','Votos','Acciones'].map(h=><th key={h} className="text-left py-2 px-3 text-xs text-gray-500 font-medium">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {items.map(c => {
                    const tipo = TIPO_COLORES[c.tipo_base]||TIPO_COLORES.BASICA;
                    return (
                      <tr key={c.id} className="border-b hover:bg-gray-50">
                        <td className="py-2 px-3 font-bold text-gray-800">{c.tipo_casilla}</td>
                        <td className="py-2 px-3"><span className="px-2 py-0.5 rounded text-xs font-bold text-white" style={{background:tipo.bg}}>{tipo.label}</span></td>
                        <td className="py-2 px-3"><div className="text-gray-700 text-xs">{c.nombre_lugar}</div>{c.localidad&&<div className="text-gray-400 text-xs">{c.localidad}</div>}</td>
                        <td className="py-2 px-3">{c.rep_nombre?<span className="text-green-700 text-xs font-semibold">✓ {c.rep_nombre}</span>:<span className="text-red-500 text-xs">⚠ Sin asignar</span>}</td>
                        <td className="py-2 px-3 text-xs text-gray-400">{c.rep_telefono||'—'}</td>
                        <td className="py-2 px-3 text-center text-xs font-bold"><span className={parseInt(c.votos_marcados)>0?'text-green-600':'text-gray-400'}>{c.votos_marcados||0}</span></td>
                        <td className="py-2 px-3">
                          <div className="flex gap-1">
                            <button onClick={()=>abrirModal(c)} className="px-2 py-1 bg-blue-600 text-white rounded text-xs font-medium hover:bg-blue-700">{c.rep_nombre?'Editar':'+ Asignar'}</button>
                            {c.rep_nombre&&<button onClick={()=>quitar(c)} className="px-2 py-1 bg-red-100 text-red-600 rounded text-xs hover:bg-red-200">✕</button>}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
      {modalRep && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="p-6 text-white rounded-t-2xl" style={{background:'linear-gradient(135deg,#14532d,#5c1022)'}}>
              <h3 className="font-bold text-lg">🗳️ Representante — Sección {modalRep.seccion} / {modalRep.tipo_casilla}</h3>
              <p className="text-sm mt-1 opacity-80">{modalRep.nombre_lugar}</p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1 uppercase">Tipo de representante</label>
                <div className="flex gap-2 mb-3">
                  {[{value:'usuario',label:'👤 Usuario'},{value:'rep_casilla',label:'📋 Rep. Casilla'},{value:'promovido',label:'🗳️ Promovido'}].map(t=>(
                    <button key={t.value} type="button"
                      onClick={()=>{setTipoRep(t.value);setFormRep(p=>({...p,user_id:'',nombre_completo:'',telefono:''}));}}
                      className={`flex-1 py-2 px-1 rounded-lg text-xs font-bold border-2 transition ${tipoRep===t.value?'bg-green-600 text-white border-green-600':'bg-white text-gray-500 border-gray-300'}`}>
                      {t.label}
                    </button>
                  ))}
                </div>
                {tipoRep && (
                  <>
                    <label className="block text-xs font-medium text-gray-600 mb-1 uppercase">Seleccionar</label>
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" value={formRep.user_id} onChange={e=>handleSelectUsuario(e.target.value)}>
                      <option value="">— Seleccionar —</option>
                      {(tipoRep==='rep_casilla'
                        ? usuarios.filter(u=>u.rol_nombre==='representante_casilla')
                        : tipoRep==='usuario'
                        ? usuarios.filter(u=>u.rol_nombre!=='representante_casilla')
                        : usuarios
                      ).map(u=><option key={u.id} value={u.id}>{u.nombre} {u.apellido_paterno} {u.apellido_materno||''} — {u.telefono}</option>)}
                    </select>
                    <p className="text-xs text-gray-400 mt-1">Al seleccionar se llena el nombre y teléfono automáticamente</p>
                  </>
                )}
              </div>
              {[{label:'Nombre completo *',key:'nombre_completo',upper:true},{label:'Teléfono',key:'telefono'},{label:'Notas',key:'notas'}].map(f=>(
                <div key={f.key}>
                  <label className="block text-xs font-medium text-gray-600 mb-1 uppercase">{f.label}</label>
                  <input className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                    value={formRep[f.key]} onChange={e=>setFormRep(p=>({...p,[f.key]:f.upper?e.target.value.toUpperCase():e.target.value}))}/>
                </div>
              ))}
              <div className="flex gap-3">
                <button onClick={()=>setModalRep(null)} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-600 text-sm hover:bg-gray-50">Cancelar</button>
                <button onClick={guardar} disabled={guardando} className="flex-1 text-white text-sm font-semibold py-2 rounded-lg hover:opacity-90 disabled:opacity-50" style={{background:'linear-gradient(135deg,#16a34a,#9b1c35)'}}>
                  {guardando?'Guardando...':'✓ Guardar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── EXTERNOS MI CASILLA ──────────────────────
function ExternosMiCasilla({ conteoNoReg }) {
  const [externos, setExternos] = useState([]);

  useEffect(() => {
    api.get('/casillas/votos/externos/mi-casilla')
      .then(r => setExternos(r.data.externos || []))
      .catch(console.error);
  }, [conteoNoReg]);

  if (externos.length === 0) return null;

  return (
    <div className="mt-4 bg-gray-50 rounded-xl border border-gray-200 p-4">
      <h3 className="text-sm font-semibold text-gray-600 mb-2">👤 Votantes no registrados en tu casilla ({externos.length})</h3>
      <div className="max-h-32 overflow-y-auto space-y-1">
        {externos.map((v,i) => (
          <div key={i} className="flex justify-between text-xs bg-white rounded-lg px-3 py-1.5 border border-gray-100">
            <span className="text-gray-600">{v.apellido_paterno} {v.apellido_materno}, {v.nombre}</span>
            <span className="text-gray-400 ml-2 flex-shrink-0">{hora(v.hora_voto)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── MI CASILLA ───────────────────────────────
function MiCasilla({ userId, esRep }) {
  const [miCasilla, setMiCasilla] = useState(null);
  const [promovidos, setPromovidos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buscando, setBuscando] = useState(false);
  const [buscar, setBuscar] = useState('');
  const [soloPendientes, setSoloPendientes] = useState(false);
  const [pagina, setPagina] = useState(1);
  const [total, setTotal] = useState(0);
  const [marcando, setMarcando] = useState(null);
  const [ultimoMarcado, setUltimoMarcado] = useState(null);
  const [modalNoReg, setModalNoReg] = useState(false);
  const [formNoReg, setFormNoReg] = useState({ nombre:'', apellido_paterno:'', apellido_materno:'' });
  const [guardandoNoReg, setGuardandoNoReg] = useState(false);
  const [conteoNoReg, setConteoNoReg] = useState(0);
  const buscarTimer = useRef(null);
  const LIMITE = 30;

  const cargarPromovidos = useCallback(async (p=1) => {
    setBuscando(true);
    try {
      const r = await api.get('/casillas/promovidos', { params:{ buscar, pagina:p, limite:LIMITE, solo_pendientes:soloPendientes } });
      setPromovidos(r.data.promovidos||[]);
      setTotal(r.data.total||0);
      setPagina(p);
    } catch(e){ console.error(e); }
    finally { setBuscando(false); setLoading(false); }
  }, [buscar, soloPendientes]);

  const cargarNoRegistrados = useCallback(async () => {
    try {
      const r = await api.get('/casillas/votos/no-registrados/conteo');
      setConteoNoReg(r.data.total||0);
    } catch(e){ console.error(e); }
  }, []);

  useEffect(() => {
    api.get('/casillas/mi-casilla').then(r=>setMiCasilla(r.data.casilla)).catch(console.error);
    cargarNoRegistrados();
  }, [userId, cargarNoRegistrados]);

  useEffect(() => {
    setLoading(true);
    clearTimeout(buscarTimer.current);
    buscarTimer.current = setTimeout(()=>cargarPromovidos(1), buscar?400:0);
    return ()=>clearTimeout(buscarTimer.current);
  }, [cargarPromovidos, buscar]);

  const marcarVoto = async (p) => {
    if (p.voto_id) return;
    if (!window.confirm(`¿Confirmar que ${p.nombre} ${p.apellido_paterno} ya votó?`)) return;
    setMarcando(p.id);
    try {
      await api.post('/casillas/votos/marcar', { promovido_id:p.id, casilla_id:miCasilla?.id||null });
      setPromovidos(prev=>prev.map(x=>x.id===p.id?{...x,voto_id:'marcado',hora_voto:new Date().toISOString()}:x));
      setUltimoMarcado(p);
      setTimeout(()=>setUltimoMarcado(null), 4000);
    } catch(e){ alert('Error: '+(e.response?.data?.error||e.message)); }
    finally { setMarcando(null); }
  };

  const registrarNoRegistrado = async () => {
    if (!formNoReg.nombre.trim()||!formNoReg.apellido_paterno.trim()) { alert('Nombre y apellido son obligatorios'); return; }
    setGuardandoNoReg(true);
    try {
      await api.post('/casillas/votos/no-registrado', { ...formNoReg, casilla_id:miCasilla?.id||null });
      setUltimoMarcado({ nombre:formNoReg.nombre, apellido_paterno:formNoReg.apellido_paterno });
      setTimeout(()=>setUltimoMarcado(null), 4000);
      setFormNoReg({ nombre:'', apellido_paterno:'', apellido_materno:'' });
      setModalNoReg(false);
      cargarNoRegistrados();
    } catch(e){ alert('Error: '+(e.response?.data?.error||e.message)); }
    finally { setGuardandoNoReg(false); }
  };

  const totalPaginas = Math.ceil(total/LIMITE);
  const votaron = promovidos.filter(p=>p.voto_id).length;
  const pendientes = promovidos.filter(p=>!p.voto_id).length;

  if (loading) return <div className="flex items-center justify-center h-64"><p className="text-gray-400">Cargando...</p></div>;

  return (
    <div>
      {miCasilla ? (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4 flex items-center gap-4">
          <span className="text-3xl">🗳️</span>
          <div>
            <p className="text-xs text-blue-600 font-bold uppercase tracking-wide">Tu casilla asignada</p>
            <p className="font-bold text-gray-800">Sección {miCasilla.seccion} — Casilla {miCasilla.tipo_casilla}</p>
            <p className="text-sm text-gray-500">{miCasilla.nombre_lugar} · {miCasilla.localidad||miCasilla.colonia}</p>
          </div>
        </div>
      ) : esRep && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4 text-red-700 text-sm">
          ⚠️ No tienes casilla asignada. Contacta al administrador.
        </div>
      )}
      {ultimoMarcado && (
        <div className="fixed top-4 right-4 bg-green-700 text-white px-4 py-3 rounded-xl z-50 shadow-lg text-sm">
          ✅ <strong>{ultimoMarcado.nombre} {ultimoMarcado.apellido_paterno}</strong> marcado como votante
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100 text-center"><p className="text-2xl font-bold text-gray-700">{total.toLocaleString()}</p><p className="text-xs text-gray-500 mt-1">Promovidos</p></div>
        <div className="bg-white rounded-xl shadow-sm p-4 border border-green-100 text-center"><p className="text-2xl font-bold text-green-600">{votaron}</p><p className="text-xs text-gray-500 mt-1">Ya votaron</p></div>
        <div className="bg-white rounded-xl shadow-sm p-4 border border-orange-100 text-center"><p className="text-2xl font-bold text-orange-500">{pendientes}</p><p className="text-xs text-gray-500 mt-1">Pendientes</p></div>
        <div className="bg-purple-50 rounded-xl shadow-sm p-4 border border-purple-200 text-center cursor-pointer hover:bg-purple-100" onClick={()=>setModalNoReg(true)}>
          <p className="text-2xl font-bold text-purple-600">{conteoNoReg}</p><p className="text-xs text-purple-600 mt-1 font-medium">+ No registrados</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-3 mb-4">
        <input className="flex-1 min-w-48 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
          placeholder="🔍 Buscar por nombre o apellido..." value={buscar} onChange={e=>setBuscar(e.target.value)} autoFocus/>
        <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
          <input type="checkbox" checked={soloPendientes} onChange={e=>setSoloPendientes(e.target.checked)}/> Solo pendientes
        </label>
        <button onClick={()=>setModalNoReg(true)} className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-semibold hover:bg-purple-700 transition">
          + Votante no registrado
        </button>
      </div>
      <div className="space-y-2">
        {buscando && <p className="text-center text-gray-400 py-4">Buscando...</p>}
        {!buscando && promovidos.length===0 && <div className="bg-white rounded-xl p-8 text-center text-gray-400 border border-gray-100">{buscar?`Sin resultados para "${buscar}"`:'No hay promovidos en este filtro'}</div>}
        {promovidos.map(p=>(
          <div key={p.id} className={`flex items-center gap-4 p-4 rounded-xl border ${p.voto_id?'bg-green-50 border-green-200':'bg-white border-gray-100'}`}>
            <span className={`px-2 py-1 rounded-lg text-xs font-bold flex-shrink-0 ${p.voto_id?'bg-green-600 text-white':'bg-blue-100 text-blue-700'}`}>{p.voto_id?'✓ VOTÓ':'⏳ PENDIENTE'}</span>
            <div className="flex-1">
              <p className="font-semibold text-gray-800 text-sm">{p.apellido_paterno} {p.apellido_materno}, {p.nombre}</p>
              <p className="text-xs text-gray-400">{p.seccion_electoral&&`Sección ${p.seccion_electoral}`}{p.telefono&&` · 📱 ${p.telefono}`}</p>
              {p.voto_id&&p.hora_voto&&<p className="text-xs text-green-600 mt-1">Votó a las {hora(p.hora_voto)}</p>}
            </div>
            {!p.voto_id&&<button onClick={()=>marcarVoto(p)} disabled={marcando===p.id} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-bold hover:bg-blue-700 transition disabled:opacity-50">{marcando===p.id?'...':'✓ Marcar voto'}</button>}
          </div>
        ))}
      </div>
      {totalPaginas>1&&(
        <div className="flex justify-center items-center gap-4 mt-6">
          <button className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40" disabled={pagina===1} onClick={()=>cargarPromovidos(pagina-1)}>‹ Anterior</button>
          <span className="text-gray-500 text-sm">Página {pagina} de {totalPaginas}</span>
          <button className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40" disabled={pagina===totalPaginas} onClick={()=>cargarPromovidos(pagina+1)}>Siguiente ›</button>
        </div>
      )}

      <ExternosMiCasilla conteoNoReg={conteoNoReg} />

      {modalNoReg&&(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="p-5 text-white rounded-t-2xl" style={{background:'linear-gradient(135deg,#7c3aed,#5c1022)'}}>
              <h3 className="font-bold text-lg">👤 Votante No Registrado</h3>
              <p className="text-sm opacity-80 mt-1">Persona que votó pero no está en el padrón del sistema</p>
            </div>
            <div className="p-6 space-y-4">
              {[{label:'Apellido Paterno *',key:'apellido_paterno'},{label:'Apellido Materno',key:'apellido_materno'},{label:'Nombre(s) *',key:'nombre'}].map(f=>(
                <div key={f.key}>
                  <label className="block text-xs font-medium text-gray-600 mb-1 uppercase">{f.label}</label>
                  <input className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    value={formNoReg[f.key]} onChange={e=>setFormNoReg(p=>({...p,[f.key]:e.target.value.toUpperCase()}))}/>
                </div>
              ))}
              <div className="flex gap-3">
                <button onClick={()=>{setModalNoReg(false);setFormNoReg({nombre:'',apellido_paterno:'',apellido_materno:''}); }} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-600 text-sm hover:bg-gray-50">Cancelar</button>
                <button onClick={registrarNoRegistrado} disabled={guardandoNoReg} className="flex-1 text-white text-sm font-semibold py-2 rounded-lg hover:opacity-90 disabled:opacity-50" style={{background:'linear-gradient(135deg,#7c3aed,#5c1022)'}}>
                  {guardandoNoReg?'Registrando...':'✓ Registrar voto'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── MONITOR EN VIVO ──────────────────────────
function MonitorCasillas() {
  const [datos, setDatos] = useState(null);
  const [loading, setLoading] = useState(true);
  const [countdown, setCountdown] = useState(INTERVALO);
  const [seccionFoco, setSeccionFoco] = useState(null);
  const [detalleSeccion, setDetalleSeccion] = useState({});
  const [cargandoDetalle, setCargandoDetalle] = useState(null);
  const intervalRef = useRef(null);
  const countdownRef = useRef(null);

  const cargar = useCallback(async () => {
    try {
      const r = await api.get('/casillas/monitor/resumen');
      setDatos(r.data); setCountdown(INTERVALO);
    } catch(e){ console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    cargar();
    intervalRef.current = setInterval(cargar, INTERVALO*1000);
    countdownRef.current = setInterval(()=>setCountdown(c=>c>0?c-1:INTERVALO), 1000);
    return ()=>{ clearInterval(intervalRef.current); clearInterval(countdownRef.current); };
  }, [cargar]);

  const verDetalle = async (seccion) => {
    if (seccionFoco===seccion) { setSeccionFoco(null); return; }
    setSeccionFoco(seccion);
    if (detalleSeccion[seccion]) return;
    setCargandoDetalle(seccion);
    try {
      const r = await api.get(`/casillas/monitor/seccion/${seccion}`);
      setDetalleSeccion(prev=>({...prev,[seccion]:r.data}));
    } catch(e){ console.error(e); }
    finally { setCargandoDetalle(null); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><p className="text-gray-400">Conectando...</p></div>;
  if (!datos) return <div className="flex items-center justify-center h-64"><p className="text-red-500">Error al cargar datos.</p></div>;

  const { global: gbl, por_seccion, ultimos_votos } = datos;
  const pctGlobal = parseFloat(gbl?.porcentaje_global||0);
  const totalVotaron = parseInt(gbl?.total_votaron||0);
  const totalPromovidos = parseInt(gbl?.total_promovidos||0);
  const totalNoReg = parseInt(gbl?.total_no_registrados||0);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <span className="text-green-600 text-xs font-bold tracking-widest">● EN VIVO</span>
          <p className="text-gray-500 text-xs mt-1">Última actualización: {new Date(datos.timestamp).toLocaleTimeString('es-MX')} · Refrescando en <span className="text-blue-600 font-bold">{countdown}s</span></p>
        </div>
        <button onClick={cargar} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-blue-600 font-medium hover:bg-gray-50">⟳ Actualizar</button>
      </div>

      {/* 4 tarjetas */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100 text-center"><p className="text-2xl font-bold text-blue-600">{totalPromovidos.toLocaleString()}</p><p className="text-xs text-gray-500 mt-1">Promovidos del sistema</p></div>
        <div className="bg-white rounded-xl shadow-sm p-4 border border-green-100 text-center"><p className="text-2xl font-bold text-green-600">{totalVotaron.toLocaleString()}</p><p className="text-xs text-gray-500 mt-1">✅ Ya votaron</p></div>
        <div className="bg-white rounded-xl shadow-sm p-4 border border-orange-100 text-center"><p className="text-2xl font-bold text-orange-500">{(totalPromovidos-totalVotaron).toLocaleString()}</p><p className="text-xs text-gray-500 mt-1">⏳ Pendientes</p></div>
        <div className="bg-gray-50 rounded-xl shadow-sm p-4 border border-gray-200 text-center"><p className="text-2xl font-bold text-gray-500">{totalNoReg.toLocaleString()}</p><p className="text-xs text-gray-400 mt-1">👤 No registrados</p><p className="text-xs text-gray-300">Solo informativo</p></div>
      </div>

      {/* Barra global — solo promovidos del sistema */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-4 border border-gray-100">
        <div className="flex justify-between text-sm mb-2">
          <span className="text-gray-500">Avance global (promovidos del sistema vs meta)</span>
          <span className={`font-bold ${colorPct(pctGlobal)}`}>{pctGlobal}%</span>
        </div>
        <div className="w-full bg-gray-100 rounded-full h-3">
          <div className={`${bgBarPct(pctGlobal)} h-3 rounded-full transition-all`} style={{width:`${Math.min(pctGlobal,100)}%`}}/>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Por sección */}
        <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
          <h3 className="font-semibold text-gray-700 mb-3">📍 Por Sección <span className="text-xs text-gray-400 font-normal">(clic para ver detalle)</span></h3>
          <div className="space-y-1 max-h-96 overflow-y-auto pr-1">
            {por_seccion?.map(s => {
              const pct = parseFloat(s.porcentaje||0);
              const abierto = seccionFoco===s.seccion;
              const detalle = detalleSeccion[s.seccion];
              return (
                <div key={s.seccion} className="rounded-lg overflow-hidden border border-gray-100">
                  <div className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50" onClick={()=>verDetalle(s.seccion)}>
                    <span className="text-blue-600 font-bold text-xs w-14 flex-shrink-0">SEC {s.seccion}</span>
                    <div className="flex-1 bg-gray-100 rounded-full h-2"><div className={`${bgBarPct(pct)} h-2 rounded-full`} style={{width:`${Math.min(pct,100)}%`}}/></div>
                    <span className={`font-bold text-xs w-10 text-right ${colorPct(pct)}`}>{pct}%</span>
                    <span className="text-gray-400 text-xs w-16 text-right">{s.votaron}/{s.meta_votos}</span>
                    <span className="text-gray-300 text-xs">{abierto?'▲':'▼'}</span>
                  </div>
                  {abierto&&(
                    <div className="border-t border-gray-100 bg-gray-50 px-3 py-2">
                      {cargandoDetalle===s.seccion?<p className="text-xs text-gray-400 py-2 text-center">Cargando...</p>:detalle?(
                        <div className="space-y-2">
                          <div>
                            <p className="text-xs font-semibold text-green-700 mb-1">✅ Del sistema ({detalle.votantes?.length||0})</p>
                            <div className="max-h-28 overflow-y-auto space-y-1">
                              {detalle.votantes?.length===0&&<p className="text-xs text-gray-400">Ninguno aún</p>}
                              {detalle.votantes?.map((v,i)=>(
                                <div key={i} className="flex justify-between text-xs bg-white rounded px-2 py-1">
                                  <span className="text-gray-700 font-medium">{v.apellido_paterno} {v.apellido_materno}, {v.nombre}</span>
                                  <span className="text-gray-400 ml-2 flex-shrink-0">{hora(v.hora_voto)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-gray-500 mb-1">👤 Externos ({detalle.externos?.length||0})</p>
                            <div className="max-h-20 overflow-y-auto space-y-1">
                              {detalle.externos?.length===0&&<p className="text-xs text-gray-400">Ninguno registrado</p>}
                              {detalle.externos?.map((v,i)=>(
                                <div key={i} className="flex justify-between text-xs bg-white rounded px-2 py-1">
                                  <span className="text-gray-500">{v.apellido_paterno} {v.apellido_materno}, {v.nombre}</span>
                                  <span className="text-gray-400 ml-2 flex-shrink-0">{hora(v.hora_voto)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      ):null}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Últimas activaciones */}
        <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
          <h3 className="font-semibold text-gray-700 mb-3">⚡ Últimas activaciones</h3>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {(!ultimos_votos||ultimos_votos.length===0)&&<p className="text-gray-400 text-sm text-center py-8">Aún no hay votos registrados</p>}
            {ultimos_votos?.map((v,i)=>(
              <div key={v.id} className={`flex items-center gap-3 p-3 rounded-xl border text-sm ${i===0?'bg-green-50 border-green-200':'bg-gray-50 border-gray-100'}`}>
                <span className="text-blue-600 text-xs font-bold whitespace-nowrap">{hora(v.marcado_en)}</span>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-800 truncate">{v.apellido_paterno} {v.apellido_materno}, {v.nombre}</p>
                  <p className="text-gray-400 text-xs">Sec. {v.seccion_electoral||'—'}{v.tipo_casilla&&` · ${v.tipo_casilla}`}{v.es_externo&&' · 👤 No registrado'}</p>
                </div>
                {i===0&&<span className="bg-green-600 text-white text-xs px-2 py-0.5 rounded font-bold flex-shrink-0">NUEVO</span>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── MÓDULO PRINCIPAL ─────────────────────────
export default function CasillasModule() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const desdeAdmin = searchParams.get('desde') === 'admin';
  const rol = user?.rol || user?.rol_nombre;
  const esRep = rol === 'representante_casilla';
  const puedeAdmin = ['superadmin','coordinador_general','coordinador_territorial'].includes(rol);
  const puedeMonitor = ['superadmin','coordinador_general','coordinador_territorial'].includes(rol);
  const [pestana, setPestana] = useState(esRep ? 'micasilla' : 'gestion');

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="text-white px-6 py-4 flex items-center justify-between shadow-lg" style={{background:'linear-gradient(135deg,#14532d,#5c1022)'}}>
        <img src={logo} alt="DOMINIUM" className="h-10"/>
        <button onClick={()=>navigate(desdeAdmin ? '/admin' : '/dashboard')} title={desdeAdmin ? 'Volver al Admin' : 'Volver al Dashboard'} className="bg-white bg-opacity-20 hover:bg-opacity-30 p-2 rounded-lg transition"><ArrowLeft size={20}/></button>
      </header>
      <main className="p-4 md:p-6 max-w-7xl mx-auto">
        <div className="mb-4">
          <h2 className="text-2xl font-bold text-gray-800">🗳️ Casillas Electorales</h2>
          <p className="text-gray-500 text-sm">Zacatlán, Puebla — Elección Municipal</p>
        </div>
        <div className="flex gap-2 mb-6 border-b border-gray-200 overflow-x-auto">
          {puedeAdmin&&<button onClick={()=>setPestana('gestion')} className={`px-5 py-3 text-sm font-semibold rounded-t-lg transition whitespace-nowrap ${pestana==='gestion'?'bg-white border border-b-white border-gray-200 text-green-700 -mb-px':'text-gray-500 hover:text-gray-700'}`}>🗳️ Gestión de Casillas</button>}
          <button onClick={()=>setPestana('micasilla')} className={`px-5 py-3 text-sm font-semibold rounded-t-lg transition whitespace-nowrap ${pestana==='micasilla'?'bg-white border border-b-white border-gray-200 text-blue-700 -mb-px':'text-gray-500 hover:text-gray-700'}`}>📋 Marcar Votos</button>
          {puedeMonitor&&<button onClick={()=>setPestana('monitor')} className={`px-5 py-3 text-sm font-semibold rounded-t-lg transition whitespace-nowrap ${pestana==='monitor'?'bg-white border border-b-white border-gray-200 text-purple-700 -mb-px':'text-gray-500 hover:text-gray-700'}`}>📡 Monitor en Vivo</button>}
        </div>
        {pestana==='gestion'&&puedeAdmin&&<GestionCasillas/>}
        {pestana==='micasilla'&&<MiCasilla userId={user?.id} esRep={esRep}/>}
        {pestana==='monitor'&&puedeMonitor&&<MonitorCasillas/>}
        <p className="text-center text-gray-400 text-xs mt-8">DOMINIUM © 2026 — Zacatlán, Puebla | GARCOR Design</p>
      </main>
    </div>
  );
}
